package one.devsbox.www.ui.devices.edit

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import kotlinx.coroutines.launch
import one.devsbox.shared.files.ALL
import one.devsbox.shared.files.Resolutions
import one.devsbox.shared.files.ResourceType
import one.devsbox.shared.files.RotationType
import one.devsbox.www.io.Repository
import one.devsbox.www.provider.NavigationCallbackProvider
import one.devsbox.www.www_portal.generated.resources.*
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState

import one.devsbox.www.ui.widgets.ScreenContentAction

@VM
class DeviceEVM : NavigationViewModel() {
    private lateinit var ctx: DeviceECtx

    var contentActions by mutableStateOf<List<ScreenContentAction>>(emptyList())
        private set

    var warningMessage by mutableStateOf<String?>(null)
        private set

    var state by mutableStateOf<DataState<DeviceECtx.Editor>>(DataState.Loading())
        private set

    var resolutionMode by mutableStateOf<ResolutionMode>(ResolutionMode.Custom)

    enum class ResolutionMode {
        Picker, Custom
    }

    private var photoPickKey: String = ""


    fun genPhotoPickKey() {
        photoPickKey = "device:${ctx.args.id}:photo"
    }

    interface Action {
        fun save()
        fun clearWarning()
        fun reload()
        fun pickPhoto()
        fun back()
    }

    val action = object : Action {
        override fun save() {
            val currentState = state.entry ?: return
            scope.launch {
                state = DataState.Loading(currentState)
                runCatching {
                    Repository.ads.device.change(
                        id = ctx.args.id,
                        change = currentState.data
                    )
                }.onSuccess {
                    ctx.nav.up()
                }.onFailure {
                    warningMessage = it.message ?: "Unknown error"
                    state = DataState.Result(currentState)
                }
            }
        }

        override fun clearWarning() {
            warningMessage = null
        }

        override fun reload() {
            loadDevice()
        }

        override fun pickPhoto() {
            genPhotoPickKey()
            ctx.navCallback.collector.pickMedia(
                nav = ctx.nav,
                key = photoPickKey,
                type = ResourceType.IMAGE.mask,
                orientation = RotationType.ALL
            )
        }

        override fun back() {
            ctx.nav.up()
        }
    }

    fun setup(ctx: DeviceECtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            loadDevice()
        } else {
            ctx.navCallback.collector.get<NavigationCallbackProvider.Task.Media>(photoPickKey) { task ->
                task.response?.also { mediaInfo ->
                    state.entry?.photo?.set(mediaInfo.id)
                }
            }
        }
    }

    private fun loadDevice() {
        state = DataState.Loading()
        scope.launch {
            runCatching {
                val locations = buildList {
                    var page = 0
                    do {
                        val result = Repository.ads.location.listing(page++)
                        addAll(result.items)
                    } while (count() < result.count)
                }
                locations to Repository.ads.device.get(ctx.args.id)
            }.onSuccess { data ->
                val (locations, device) = data
                Resolutions.all.firstOrNull {
                    it.w == device.content.config.screenW && it.h == device.content.config.screenH
                }?.also {
                    resolutionMode = ResolutionMode.Picker
                } ?: run {
                    resolutionMode = ResolutionMode.Custom
                }
                state = DataState.Result(
                    DeviceECtx.Editor(
                        scope = scope,
                        device = device,
                        locations = locations
                    )
                )
                setupActions()
            }.onFailure {
                state = DataState.Error(it.message ?: "Unknown error")
                contentActions = emptyList()
            }
        }
    }

    private fun setupActions() {
        contentActions = buildList {
            add(
                ScreenContentAction(
                    text = Res.string.ui_devices_edit_deviceeui_btn_save,
                    color = ctx.colorPrimaryContainer,
                    icon = Svg.ContentSaveOutline
                ) {
                    action.save()
                }
            )
        }
    }

    override fun onRelease() {}
}

